import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import MetricsCards from './MetricsCards';
import DateRangePicker, { DateRangePreset } from './DateRangePicker';
import SearchAnalytics from './SearchAnalytics';
import BulkMessageAnalytics from './BulkMessageAnalytics';
import TokenUsageAnalytics from './TokenUsageAnalytics';
import { UserStat } from '../../../../services/adminService';
import {
    fetchOverviewMetrics,
    fetchSearchAnalytics,
    fetchBulkMessageAnalytics,
    fetchTokenUsageAnalytics,
    fetchDailyStats,
    OverviewMetrics,
    SearchHistoryRecord,
    BulkMessageRecord,
    TokenUsageRecord,
    DailyStats,
} from '../../../../services/analyticsService';

interface AnalyticsDashboardProps {
    users?: UserStat[];
}

const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ users = [] }) => {
    const [selectedPreset, setSelectedPreset] = useState<DateRangePreset>('30days');
    const [isLoading, setIsLoading] = useState(true);

    // Data states
    const [metrics, setMetrics] = useState<OverviewMetrics>({
        totalSearches: 0,
        totalDispatches: 0,
        totalTokens: 0,
        totalCost: 0,
    });
    const [searchData, setSearchData] = useState<SearchHistoryRecord[]>([]);
    const [bulkData, setBulkData] = useState<BulkMessageRecord[]>([]);
    const [tokenData, setTokenData] = useState<TokenUsageRecord[]>([]);
    const [dailyStats, setDailyStats] = useState<DailyStats[]>([]);

    const loadAnalyticsData = async (preset: DateRangePreset) => {
        setIsLoading(true);
        try {
            // Calculate date range
            let startDate: Date | undefined;
            let endDate: Date | undefined = new Date();

            if (preset !== 'all') {
                startDate = new Date();
                switch (preset) {
                    case 'today':
                        startDate = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
                        break;
                    case '7days':
                        startDate.setDate(startDate.getDate() - 7);
                        break;
                    case '30days':
                        startDate.setDate(startDate.getDate() - 30);
                        break;
                }
            }

            // Fetch all data in parallel
            const [overviewData, searchHistory, bulkHistory, tokenHistory, dailyStatsData] = await Promise.all([
                fetchOverviewMetrics(preset === 'all' ? '30days' : preset),
                fetchSearchAnalytics(startDate, endDate),
                fetchBulkMessageAnalytics(startDate, endDate),
                fetchTokenUsageAnalytics(startDate, endDate),
                fetchDailyStats(startDate, endDate),
            ]);

            setMetrics(overviewData);
            setSearchData(searchHistory);
            setBulkData(bulkHistory);
            setTokenData(tokenHistory);
            setDailyStats(dailyStatsData);
        } catch (error) {
            console.error('Error loading analytics:', error);
            toast.error('Erro ao carregar analytics');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAnalyticsData(selectedPreset);
    }, [selectedPreset]);

    const handlePresetChange = (preset: DateRangePreset) => {
        setSelectedPreset(preset);
    };

    // Prepare daily data for charts
    const searchDailyData = dailyStats.map(d => ({ date: d.date, searches: d.searches }));
    const bulkDailyData = dailyStats.map(d => ({
        date: d.date,
        dispatches: d.dispatches,
        messagesSent: d.messagesSent,
        messagesFailed: d.messagesFailed,
    }));
    const tokenDailyData = dailyStats.map(d => ({ date: d.date, tokens: d.tokens, cost: d.cost }));

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="flex flex-col items-center">
                    <span className="material-symbols-outlined text-4xl text-primary animate-spin">sync</span>
                    <p className="mt-4 text-gray-600 dark:text-gray-400">Carregando analytics...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header with Date Range Picker */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
                        📊 Analytics & Uso
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">
                        Análise detalhada de pesquisas, disparos e uso de tokens
                    </p>
                </div>
                <DateRangePicker
                    selectedPreset={selectedPreset}
                    onPresetChange={handlePresetChange}
                />
            </div>

            {/* Metrics Cards */}
            <MetricsCards
                totalSearches={metrics.totalSearches}
                totalDispatches={metrics.totalDispatches}
                totalTokens={metrics.totalTokens}
                totalCost={metrics.totalCost}
                searchesChange={metrics.searchesChange}
                dispatchesChange={metrics.dispatchesChange}
                tokensChange={metrics.tokensChange}
                costChange={metrics.costChange}
            />

            {/* Analytics Sections */}
            <div className="space-y-8">
                <SearchAnalytics data={searchData} dailyData={searchDailyData} />
                <BulkMessageAnalytics data={bulkData} dailyData={bulkDailyData} />
                <TokenUsageAnalytics data={tokenData} dailyData={tokenDailyData} users={users} />
            </div>
        </div>
    );
};

export default AnalyticsDashboard;
